import { useParams, useNavigate } from "react-router";
import z from "zod";
import { removeActivityOccurrence, useGroup } from "@/api/Activities";
import { Loading, LoadingOverlay } from "@medoma/components/Loading/Loading";
import { deducedError, displayErrorMessageAlert } from "@/Utils/ErrorUtils";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { useLingui } from "@lingui/react/macro";
import { Heading } from "@medoma/components/Heading/Heading";
import { ActivityTitle } from "@/components/ActivityTitle/ActivityTitle";
import styles from "./GroupDetails.module.scss";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import {
  activityOccurrenceStatusSchema,
  timeOfDayDictionary,
  timeOfDaySchema,
  groupStatusSchema,
  getGroupOrVisitStatus,
  type IGroupOfActivityOccurrence,
} from "@medoma/models/activities";
import { patientStatusSchema } from "@medoma/models/patients";
import { StatusTag } from "@/components/StatusTag/StatusTag";
import BinIcon from "@medoma/components/icons/BinIcon";
import { IconButton } from "@medoma/components/Button/Button";
import PlusIcon from "@medoma/components/icons/PlusIcon";
import {
  ActionCardLink,
  ActionCardButton,
} from "@/components/ActionCard/ActionCard";
import { UnstyledLink } from "@medoma/components/Link/Link";
import { useState } from "react";
import { DialogPanel } from "@medoma/components/DialogPanel/DialogPanel";
import { AddActivityInGroupForm } from "@/forms/AddActivity/AddActivityInGroupForm";
import { AssignShiftAction } from "@/components/AssignShiftAction/AssignShiftAction";
import { getUnfulfilledRequirements } from "@medoma/models/activityRequirements";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { employeesRoutesQueryOptions, getRouteFromGroup } from "@/api/Routes";
import { useCurrentEmployee } from "@/api/Employee";
import { startOfToday } from "date-fns";
import { Text } from "@medoma/components/Text/Text";
import { dateName } from "@/Utils/DateUtils";
import { formattedTimeSpan } from "@/components/Time/timeUtils";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { activityCategoryDictionary } from "@/api/Activities";
import { AssigneesChips } from "@/components/AssigneesChips/AssigneesChips";

export const GroupDetails = () => {
  const { groupId } = z.object({ groupId: z.string() }).parse(useParams());
  const { data: group, isPending, isError, error } = useGroup(groupId);
  const { t } = useLingui();
  const [addActivityInGroupDialogIsOpen, setAddActivityInGroupDialogIsOpen] =
    useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: currentEmployee, isPending: isCurrentEmployeePending } =
    useCurrentEmployee();
  // If fetching the current employee fails, we fall back to null — no competence
  // is pre-seeded and the user has to add one manually in the form.
  const currentEmployeeCompetence = currentEmployee?.competence ?? null;

  const { mutate: deleteOccurrence, isPending: isDeletingOccurrence } =
    useMutation({
      mutationFn: ({
        activityId,
        occurrenceId,
      }: {
        activityId: string;
        occurrenceId: string;
        isLastOccurrenceInGroup: boolean;
      }) => removeActivityOccurrence(activityId, occurrenceId),
      onSuccess: async (_, { isLastOccurrenceInGroup }) => {
        await queryClient.invalidateQueries();
        if (isLastOccurrenceInGroup) {
          void navigate("..");
        }
      },
      onError: (error) => {
        displayErrorMessageAlert(
          `${t`Kunde inte ta bort aktivitet`}: ${deducedError(error)}`,
        );
      },
    });

  const confirmRemoveActivityOccurrence = ({
    occurrence,
    isLastOccurrenceInGroup,
  }: {
    occurrence: IGroupOfActivityOccurrence["occurrences"][number];
    isLastOccurrenceInGroup: boolean;
  }) => {
    const { activityId, id: occurrenceId, title, recurring } = occurrence;
    const message = recurring
      ? t`Är du säker på att du vill ta bort det här aktivitetstillfället för aktiviteten "${title}"?`
      : t`Är du säker på att du vill ta bort aktiviteten "${title}"?`;
    const confirmed = window.confirm(message);
    if (confirmed) {
      deleteOccurrence({ activityId, occurrenceId, isLastOccurrenceInGroup });
    }
  };

  const onAddActivityInGroupSuccess = async () => {
    await queryClient.invalidateQueries();
    setAddActivityInGroupDialogIsOpen(false);
  };

  if (isPending) {
    return <Loading message={t`Hämtar besök`} />;
  }

  if (isError) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta besöket.`} ${deducedError(error)}`}
      />
    );
  }

  const {
    occurrences,
    start,
    end,
    visitStatus,
    timeOfDay,
    assignees,
    patient,
    requiredCompetences,
  } = group;
  const isOngoing = visitStatus === groupStatusSchema.enum.ongoing;
  const isFinished = visitStatus === groupStatusSchema.enum.finished;
  const isCancelled = visitStatus === groupStatusSchema.enum.cancelled;
  const isAnyTimeOfDay = timeOfDay === timeOfDaySchema.enum.Any;
  const patientSiteId =
    patient.status === patientStatusSchema.enum.deleted
      ? undefined
      : patient.site.id;

  // Finished groups should not be altered, and ongoing groups should be handled in the ongoing visit's page,
  // where it is possible to add Just-In-Time activities instead
  const canAddActivityInGroup = !isFinished && !isOngoing && !isCancelled;
  const canManageAssignees = !isFinished && !isOngoing && !isCancelled;
  // A group without a visitStatus reads as notReady ("Att planera") —
  // mirrors what individual occurrences already surface in the same
  // not-yet-planned state.
  const displayStatus =
    getGroupOrVisitStatus(group) ??
    activityOccurrenceStatusSchema.enum.notReady;

  return (
    <>
      <div className={styles.groupDetails}>
        <div className={styles.card}>
          <div className={styles.header}>
            <CategoryIcon category={group.category} />
            <Heading level="h1" size="h2" weight="regular">
              {t(activityCategoryDictionary[group.category])}
            </Heading>
          </div>
          <div className={styles.timeAndStatus}>
            <div className={styles.timeAndDate}>
              <Text element="span" weight="bold" size="large">
                {isAnyTimeOfDay
                  ? t(timeOfDayDictionary.Any.short)
                  : formattedTimeSpan(start, end)}
              </Text>
              <Text element="span" weight="regular" size="base">
                {dateName(start)}
              </Text>
            </div>
            <div className={styles.statusAndAssignees}>
              <StatusTag
                status={displayStatus}
                variant="icon-and-text"
                size="small"
              />
              {assignees.length > 0 ? (
                <AssigneesChips
                  assignees={assignees}
                  target={{ kind: "group", groupId }}
                  canManageAssignees={canManageAssignees}
                />
              ) : (
                requiredCompetences.map((competence) => (
                  <CompetenceChip
                    key={competence}
                    competence={competence}
                    state="unassigned"
                  />
                ))
              )}
            </div>
          </div>
          <ul className={styles.occurrences}>
            {occurrences.map((occurrence) => {
              const canRemoveOccurrence =
                occurrence.status !==
                activityOccurrenceStatusSchema.enum.finished;

              return (
                <li
                  key={`${occurrence.activityId}-${occurrence.id}`}
                  className={styles.occurrence}
                >
                  <UnstyledLink
                    to={`../${occurrence.activityId}/occurrences/${occurrence.id}/edit`}
                    className={styles.occurrenceLink}
                  >
                    <ActivityTitle
                      activityOccurrence={occurrence}
                      size="large"
                      showStatus={true}
                    />
                  </UnstyledLink>
                  {canRemoveOccurrence ? (
                    <div className={styles.removeOccurrenceButton}>
                      <IconButton
                        aria-label={t`Ta bort aktivitet`}
                        onClick={() =>
                          confirmRemoveActivityOccurrence({
                            occurrence,
                            isLastOccurrenceInGroup: occurrences.length === 1,
                          })
                        }
                        disabled={isDeletingOccurrence}
                      >
                        <LoadingOverlay show={isDeletingOccurrence}>
                          <BinIcon />
                        </LoadingOverlay>
                      </IconButton>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Visits that are Ongoing should not be handled here, but in the VisitDetails page */}
        {isOngoing ? (
          <GoToVisitActionCard groupId={groupId} />
        ) : canAddActivityInGroup ? (
          <>
            <ActionCardButton
              icon={<PlusIcon />}
              title={t`Lägg till aktivitet i besöket`}
              onClick={() => setAddActivityInGroupDialogIsOpen(true)}
            />
            {patientSiteId ? (
              <AssignShiftAction
                target={{ kind: "group", groupId }}
                assignedShiftIds={assignees.map(({ id }) => id)}
                shiftsFromDate={start}
                unfulfilledRequirements={getUnfulfilledRequirements(group)}
                patientSiteId={patientSiteId}
              />
            ) : null}
          </>
        ) : null}
      </div>

      <DialogPanel
        isOpen={addActivityInGroupDialogIsOpen}
        onClose={() => setAddActivityInGroupDialogIsOpen(false)}
        title={t`Lägg till aktivitet`}
        position="bottom"
      >
        {isCurrentEmployeePending ? (
          <Loading message={t`Hämtar medarbetare`} />
        ) : (
          <AddActivityInGroupForm
            groupId={groupId}
            onSuccess={onAddActivityInGroupSuccess}
            currentEmployeeCompetence={currentEmployeeCompetence}
          />
        )}
      </DialogPanel>
    </>
  );
};

const GoToVisitActionCard = ({ groupId }: { groupId: string }) => {
  const { t } = useLingui();
  const { data: employeesRoutes } = useQuery(
    employeesRoutesQueryOptions(startOfToday()),
  );

  if (!employeesRoutes) {
    return (
      <ActionCardLink
        to={"/routesAndActivities"}
        title={t`Gå till pågående besök`}
      />
    );
  }
  const route = getRouteFromGroup(employeesRoutes, groupId);

  // If the route is not found, it doesn't make sense to show any link
  if (!route) {
    return null;
  }

  return (
    <ActionCardLink
      to={`/routesAndActivities/${route.id}/visits/${groupId}`}
      title={t`Gå till pågående besök`}
    />
  );
};
